import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Linkedin } from "lucide-react";
import { api } from "../../lib/api";
import { Roundel } from "../brand/icons";

const DEFAULTS = {
  footer_address: "48 Curzon Street, London W1J 7UL, UK",
  footer_address_la: "12304 Santa Monica Blvd. Suite 302, Los Angeles CA 90025",
  linkedin_url: "https://www.linkedin.com/company/78479670",
  instagram_url: "https://www.instagram.com/artofthepantheon",
  spv_url: "#",
  abos_url: "https://abunchofstuff.com",
  spv_logo: "/assets/logos/spv-logo.png",
  abos_logo: "/assets/logos/abos-logo.png",
  copyright_line: "All rights reserved © Pantheon Art Limited 2026",
};

export const Footer = () => {
  const [settings, setSettings] = useState(DEFAULTS);

  useEffect(() => {
    api
      .get("/settings/site")
      .then((res) => res.data && setSettings({ ...DEFAULTS, ...res.data }))
      .catch(() => {});
  }, []);

  return (
    <footer data-testid="site-footer" className="hairline-t bg-offwhite text-charcoal">
      <div className="grid gap-12 px-5 py-14 md:grid-cols-12 lg:px-10 lg:py-20">
        <div className="md:col-span-3">
          <Roundel className="h-28 w-28 text-charcoal" data-testid="footer-roundel" />
        </div>
        <nav className="flex flex-col gap-3 md:col-span-4" aria-label="Legal">
          <Link
            to="/privacy-policy"
            data-testid="footer-privacy-link"
            className="font-serifbrand text-lg transition-opacity duration-300 hover:opacity-60"
          >
            Privacy Policy
          </Link>
          <Link
            to="/terms-of-service"
            data-testid="footer-terms-link"
            className="font-serifbrand text-lg transition-opacity duration-300 hover:opacity-60"
          >
            Terms of Service
          </Link>
        </nav>
        <div className="md:col-span-5 md:text-right">
          <p className="h-condensed text-base tracking-[0.06em]" data-testid="footer-subscribe-heading">
            Subscribe for latest news &amp; offers
          </p>
          <div className="mt-6 flex gap-5 md:justify-end">
            <a
              href={settings.instagram_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-instagram-link"
              aria-label="Pantheon Art on Instagram"
              className="transition-opacity duration-300 hover:opacity-60"
            >
              <Instagram className="h-5 w-5" strokeWidth={1.5} />
            </a>
            <a
              href={settings.linkedin_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-linkedin-link"
              aria-label="Pantheon Art on LinkedIn"
              className="transition-opacity duration-300 hover:opacity-60"
            >
              <Linkedin className="h-5 w-5" strokeWidth={1.5} />
            </a>
          </div>
        </div>
      </div>

      <div className="hairline-t grid gap-10 px-5 py-12 md:grid-cols-12 lg:px-10 lg:py-14">
        <div className="md:col-span-3">
          <p className="h-condensed text-sm tracking-[0.08em]">London</p>
          <p className="font-serifbrand mt-3 max-w-[24ch] text-lg leading-snug" data-testid="footer-address">
            {settings.footer_address}
          </p>
        </div>
      </div>

      <div className="hairline-t flex flex-wrap items-center justify-between gap-6 px-5 py-5 lg:px-10">
        <p className="font-condensed text-xs font-semibold uppercase tracking-[0.14em]" data-testid="footer-copyright">
          {settings.copyright_line}
        </p>
        <div className="flex items-center gap-8">
          <a
            href={settings.spv_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-spv-link"
            aria-label="SPV"
          >
            <img src={settings.spv_logo} alt="SPV" className="h-6 w-auto select-none" draggable={false} />
          </a>
          <a
            href={settings.abos_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-abos-link"
            aria-label="A Bunch Of Stuff"
          >
            <img src={settings.abos_logo} alt="A Bunch Of Stuff" className="h-9 w-auto select-none" draggable={false} />
          </a>
        </div>
      </div>
    </footer>
  );
};
